'use client';

import { useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

import LineLogo from '@/svgs/line-logo.svg';
import TickLogo from '@/svgs/tick-logo.svg';

const MotionLineLogo = motion(LineLogo);
const MotionTickLogo = motion(TickLogo);

type Props = {
  spinnerOnly?: boolean;
};

const Loading: React.FC<Props> = ({ spinnerOnly = false }) => {
  const circleControls = useAnimationControls();
  const tick1Controls = useAnimationControls();
  const tick2Controls = useAnimationControls();
  const lineControls = useAnimationControls();

  useEffect(() => {
    if (spinnerOnly) {
      return;
    }

    const runAnimation = async () => {
      // Phase 1: shrink
      await Promise.all([
        tick1Controls.start({
          rotateX: 180,
          y: 37,
          x: 8,
          fill: '#2145D7',
          width: 47,
          height: 48,
          transition: {
            duration: 2.5,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 11.25,
            damping: 7.5,
          },
        }),
        tick2Controls.start({
          opacity: 1,
          rotateY: 180,
          x: 30,
          y: -13,
          fill: '#2145D7',
          width: 47,
          height: 48,
          transition: {
            duration: 2.5,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 11.25,
            damping: 7.5,
          },
        }),
        lineControls.start({
          x: 26,
          y: 20,
          width: 16,
          height: 20,
          fill: '#2145D7',
          transition: {
            duration: 2.5,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 11.25,
            damping: 7.5,
          },
        }),
        circleControls.start({
          width: 148,
          height: 148,
          borderRadius: '50%',
          backgroundColor: '#E0E6FA',
          transition: {
            duration: 2.5,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 11.25,
            damping: 7.5,
          },
        }),
      ]);

      // Wait a bit
      await new Promise((r) => setTimeout(r, 400));

      // Phase 2: expand
      await Promise.all([
        tick1Controls.start({
          width: 114,
          x: 23,
          y: 67,
          height: 117,
          stroke: ['#2145D7', '#ffffff'],
          fill: 'none',
          transition: {
            duration: 1,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 80,
            damping: 20,
          },
        }),
        tick2Controls.start({
          width: 114,
          x: 79,
          y: -53,
          height: 117,
          stroke: ['#2145D7', '#ffffff'],
          fill: 'none',
          transition: {
            duration: 1,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 80,
            damping: 20,
          },
        }),
        lineControls.start({
          x: 75,
          y: 19,
          width: 40,
          height: 47,
          stroke: ['#2145D7', '#ffffff'],
          fill: 'none',
          transition: {
            duration: 1,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 80,
            damping: 20,
          },
        }),
        circleControls.start({
          width: '300vmax',
          height: '200vmax',
          backgroundColor: '#647EE4',
          rotate: -100,
          rotateX: -180,
          transition: {
            duration: 1.5,
            ease: 'easeInOut',
            mass: 1,
            stiffness: 80,
            damping: 20,
          },
        }),
      ]);
    };

    void runAnimation();
  }, [circleControls, lineControls, spinnerOnly, tick1Controls, tick2Controls]);

  if (spinnerOnly) {
    return (
      <div className="w-full h-dvh bg-white flex items-center justify-center">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-[#E0E6FA] border-t-[#2145D7]"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-dvh bg-white overflow-hidden flex items-center justify-center"
      style={{ perspective: '1200px' }}
    >
      {/* Circle */}
      <motion.div
        animate={circleControls}
        initial={{
          width: '200vmax',
          height: '200vmax',
          borderRadius: '50%',
          backgroundColor: '#E0E6FA',
        }}
        style={{
          transformStyle: 'preserve-3d',
          position: 'absolute',
          top: '50%',
          left: '50%',
          x: '-50%',
          y: '-50%',
        }}
      />

      {/* Logos */}
      <motion.div
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          stroke: '#2145D7',
          zIndex: 10,
          width: 30,
          height: 30,
        }}
      >
        <MotionTickLogo
          animate={tick1Controls}
          initial={{
            position: 'absolute',
            rotateX: 0,
            y: 0,
            width: 22,
            height: 23,
            stroke: '#2145D7',
          }}
          style={{
            transformStyle: 'preserve-3d',
            transformOrigin: 'top center',
          }}
        />

        <MotionTickLogo
          animate={tick2Controls}
          initial={{
            position: 'absolute',
            rotateY: 0,
            x: 0,
            width: 22,
            height: 23,
            opacity: 0,
            stroke: '#2145D7',
          }}
          style={{
            transformStyle: 'preserve-3d',
            transformOrigin: 'left center',
          }}
        />

        <MotionLineLogo
          animate={lineControls}
          initial={{
            x: 1,
            y: 18,
            position: 'absolute',
            width: 8,
            height: 10,
            stroke: '#2145D7',
          }}
        />
      </motion.div>
    </div>
  );
};

export default Loading;
